import { Eye } from '@tamagui/lucide-icons-2'
import { useQueryClient } from '@tanstack/react-query'
import { useOrgId } from 'helpers'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import Button from 'components/button'
import { SizableText, YStack } from 'tamagui'
import { useUserData } from '../../auth'
import { CheckItem } from '../CheckItem'
import { useOnboarding } from '../OnboardingContext'

const viewedKey_PREFIX = 'onboarding-wishlist-viewed'

export default function WishlistGuide() {
    const orgId = useOrgId()
    const viewedKey = `${viewedKey_PREFIX}-${orgId}`
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const queryClient = useQueryClient()
    const { user } = useUserData()
    const { progress, setCurrentStep, refetchProgress, startStepTour, isInTourStepFlow, testPurchaserId, setTestPurchaserId } = useOnboarding()
    const [hasViewedWishlist, setHasViewedWishlist] = useState(() => localStorage.getItem(viewedKey) === 'true')
    const [phase2Stage, setPhase2Stage] = useState<'idle' | 'viewClient' | 'viewClientWishlist' | 'done'>('idle')
    const tourWasActiveRef = useRef(false)

    const hasPurchaser = progress?.hasPurchaser ?? false
    const hasWishlistFilled = progress?.hasWishlistFilled ?? false

    // Auto-start phase 2 when returning from onboarding wishlist submission
    const autoStartedRef = useRef(false)
    useEffect(() => {
        if (
            searchParams.get('from') === 'wishlist' &&
            !autoStartedRef.current &&
            !hasViewedWishlist
        ) {
            autoStartedRef.current = true
            const purchaserId = searchParams.get('purchaserId')
            if (purchaserId) setTestPurchaserId(purchaserId)
            setSearchParams({}, { replace: true })
            void (async () => {
                await queryClient.invalidateQueries({ queryKey: [['purchaser']] })
                await queryClient.refetchQueries({ queryKey: [['purchaser']] })
                await refetchProgress()
                setTimeout(() => {
                    setPhase2Stage('viewClient')
                    startStepTour('viewClient')
                }, 500)
            })()
        }
    }, [searchParams, hasViewedWishlist, refetchProgress, startStepTour, setSearchParams, queryClient, setTestPurchaserId])

    const startPhase2 = useCallback(() => {
        tourWasActiveRef.current = false
        setPhase2Stage('viewClient')
        startStepTour('viewClient')
    }, [startStepTour])

    // Track when tour becomes active during phase 2
    useEffect(() => {
        if (isInTourStepFlow && (phase2Stage === 'viewClient' || phase2Stage === 'viewClientWishlist')) {
            tourWasActiveRef.current = true
        }
    }, [isInTourStepFlow, phase2Stage])

    // When viewClient tour exits (user followed all steps including wishlist tab), mark as done
    useEffect(() => {
        if (phase2Stage === 'viewClient' && !isInTourStepFlow && tourWasActiveRef.current) {
            tourWasActiveRef.current = false
            setPhase2Stage('done')
            localStorage.setItem(viewedKey, 'true')
            setHasViewedWishlist(true)
        }
    }, [phase2Stage, isInTourStepFlow])

    // Auto-tick when user navigates to the wishlist tab of the test client (with or without tour)
    const purchaserId = progress?.firstPurchaserId ?? testPurchaserId
    useEffect(() => {
        if (hasViewedWishlist || !purchaserId || !hasWishlistFilled) return
        const wishlistPath = `/${orgId}/clients/${purchaserId}/wishlist`
        if (location.pathname === wishlistPath || location.pathname.startsWith(wishlistPath + '/')) {
            setPhase2Stage('done')
            localStorage.setItem(viewedKey, 'true')
            setHasViewedWishlist(true)
        }
    }, [location.pathname, hasViewedWishlist, purchaserId, orgId, hasWishlistFilled])

    const allDone = hasWishlistFilled && hasViewedWishlist

    return (
        <YStack gap="$4">
            <SizableText size="$6" fontWeight="700">
                Try Your Public Wishlist
            </SizableText>

            {!hasWishlistFilled ? (
                <SizableText size="$3" color="$colorSoft">
                    This is the form you share with prospective buyers. Try it out with some dummy info to see how it works end-to-end.
                </SizableText>
            ) : !hasViewedWishlist ? (
                <SizableText size="$3" color="$colorSoft">
                    A new client was created from the wishlist. Now see what their data looks like from your side.
                </SizableText>
            ) : (
                <SizableText size="$3" color="$colorSoft">
                    You have seen how the wishlist works end-to-end — from client submission to your internal view.
                </SizableText>
            )}

            {/* Sub-steps checklist */}
            <YStack gap="$2" padding="$3" backgroundColor="$backgroundStrong" borderRadius={14} borderWidth={1} borderColor="$borderColor">
                <CheckItem checked={hasWishlistFilled} label="Fill out the wishlist as a test client" />
                <CheckItem checked={hasPurchaser} label="New client appears in Clients tab" />
                <CheckItem checked={hasViewedWishlist} label="View your client's wishlist data" />
            </YStack>

            {/* Phase 1: Fill the wishlist */}
            {!hasWishlistFilled && (
                <>
                    <Button
                        size="$4"
                        theme="primary"
                        onPress={() => navigate(`/${orgId}/wishlist?onboarding=true&email=${encodeURIComponent(user.email)}`)}
                    >
                        Show me
                    </Button>
                    {hasPurchaser && (
                        <Button size="$3" variant="outlined" onPress={() => refetchProgress()}>
                            Refresh Status
                        </Button>
                    )}
                </>
            )}

            {/* Phase 2: View client data */}
            {hasWishlistFilled && !hasViewedWishlist && (
                <Button
                    size="$4"
                    theme="primary"
                    onPress={startPhase2}
                    icon={<Eye size={16} color="white" />}
                >
                    View your client
                </Button>
            )}

            {/* Done */}
            {allDone && (
                <Button
                    size="$3"
                    variant="outlined"
                    onPress={() => setCurrentStep(4)}
                >
                    Next step
                </Button>
            )}
        </YStack>
    )
}
